import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { tasksService } from '@/services/tasks.service';
import { clientsService } from '@/services/clients.service';
import { projectsService } from '@/services/projects.service';
import type { Priority, Client, Project } from '@/types';

function NewTaskContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    clientId: '',
    projectId: '',
    priority: 'medium' as Priority,
    deadline: '',
  });
  const [hasDeadline, setHasDeadline] = useState(false);
  const [isFromMeeting, setIsFromMeeting] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadClientsAndProjects();
  }, []);

  useEffect(() => {
    // Pre-fill form from URL parameters (e.g., from meeting action items)
    const title = searchParams.get('title');
    const clientId = searchParams.get('clientId');
    const projectId = searchParams.get('projectId');
    const deadline = searchParams.get('deadline');
    const meetingId = searchParams.get('meetingId');

    if (title || clientId || projectId || deadline || meetingId) {
      setFormData((prev) => ({
        ...prev,
        title: title || prev.title,
        clientId: clientId || prev.clientId,
        projectId: projectId || prev.projectId,
        deadline: deadline || prev.deadline,
      }));

      if (deadline) {
        setHasDeadline(true);
      }

      if (meetingId) {
        setIsFromMeeting(true);
      }
    }
  }, [searchParams]);

  const loadClientsAndProjects = async () => {
    try {
      const [clientsData, projectsData] = await Promise.all([
        clientsService.getClients(),
        projectsService.getProjects(),
      ]);
      setClients(clientsData);
      setProjects(projectsData);
    } catch (err: any) {
      console.error('Error loading clients and projects:', err);
      setError(err.message || 'Failed to load clients and projects');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const payload: any = {
        title: formData.title,
        priority: formData.priority,
        status: 'not-started',
      };

      // Only include optional fields if they have values
      if (formData.description) payload.description = formData.description;
      if (formData.clientId) payload.clientId = formData.clientId;
      if (formData.projectId) payload.projectId = formData.projectId;
      if (formData.deadline) {
        // Convert YYYY-MM-DD to ISO 8601 date-time format
        payload.deadline = new Date(formData.deadline).toISOString();
      }

      await tasksService.createTask(payload);
      navigate('/tasks');
    } catch (err: any) {
      console.error('Error creating task:', err);
      setError(err.message || 'Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = formData.clientId
    ? projects.filter((p) => p.clientId === formData.clientId)
    : projects;

  const meetingId = searchParams.get('meetingId');

  return (
    <AppLayout>
      <Breadcrumb
        items={[
          { label: 'Tasks', href: '/tasks' },
          ...(isFromMeeting
            ? [
                { label: 'Meetings', href: '/meetings' },
                { label: `Meeting ${meetingId}`, href: `/meetings/${meetingId}` },
              ]
            : []),
          { label: 'New Task' },
        ]}
      />

      <div className="max-w-2xl">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-3xl font-bold text-gray-900">
            {isFromMeeting ? 'Create Task from Meeting' : 'Add New Task'}
          </h1>
          {isFromMeeting && (
            <span className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded-full">
              From Meeting Action Item
            </span>
          )}
        </div>
        <p className="text-gray-600 mb-8">
          {isFromMeeting
            ? 'Convert meeting action item into a trackable task'
            : 'Create a new task'}
        </p>

        {error && (
          <div className="border border-border rounded-lg p-4 p-4 mb-6 border-l-4 border-red-500">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="font-semibold text-gray-900">Error</h3>
                <p className="text-gray-600 text-sm">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="border border-border rounded-lg p-4 space-y-6">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Task Title *
            </label>
            <input
              id="title"
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
              placeholder="Design homepage mockups"
              disabled={loading}
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
              placeholder="Additional details about this task..."
              disabled={loading}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="clientId" className="block text-sm font-medium text-gray-700 mb-1">
                Client
              </label>
              <select
                id="clientId"
                value={formData.clientId}
                onChange={(e) =>
                  setFormData({ ...formData, clientId: e.target.value, projectId: '' })
                }
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                disabled={loading}
              >
                <option value="">No client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="projectId" className="block text-sm font-medium text-gray-700 mb-1">
                Project
              </label>
              <select
                id="projectId"
                value={formData.projectId}
                onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                disabled={loading}
              >
                <option value="">No project</option>
                {filteredProjects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">
                Priority *
              </label>
              <select
                id="priority"
                required
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as Priority })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                disabled={loading}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="deadline" className="block text-sm font-medium text-gray-700">
                  Deadline
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setHasDeadline(!hasDeadline);
                    if (hasDeadline) {
                      setFormData({ ...formData, deadline: '' });
                    }
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    hasDeadline ? 'bg-flowky-primary' : 'bg-gray-300'
                  }`}
                  disabled={loading}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      hasDeadline ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {hasDeadline && (
                <input
                  id="deadline"
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                  disabled={loading}
                />
              )}
              {!hasDeadline && (
                <div className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-400 text-sm">
                  No deadline
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              {loading ? 'Creating...' : 'Create Task'}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              disabled={loading}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-white/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}

export default function NewTaskPage() {
  return (
    <ProtectedRoute>
      <NewTaskContent />
    </ProtectedRoute>
  );
}
